/**
 * Railgun Beam Renderer — multi-pass glowing line for hitscan beam display
 * + edgeguard charge bar at turret tip + aim direction line.
 *
 * Beam path is stored on GameState.edgeguardBeam. This renderer reads it
 * each frame, draws 4 layered line passes (wide glow → bright core → white),
 * bounce point circles, and a terminus explosion. Alpha fades with timer.
 *
 * Charge bar: thin bar at turret tip, fills cyan → white, flash on full.
 * Aim line: subtle direction indicator from turret when charged.
 *
 * Lives on the world container (world-space coordinates).
 */

import { Container, Graphics } from 'pixi.js';

export interface BeamState {
  path: { x: number; y: number }[];
  timer: number;
  maxTimer: number;
  width: number;
  color: number;
}

export interface EdgeguardChargeState {
  chargeT: number;     // 0..1 charge progress
  charged: boolean;    // true when chargeT >= 1
  fired: boolean;      // true after beam fired
  attackerX: number;
  attackerY: number;
  attackerAngle: number; // radians, 0=right, PI=left
  turretLength: number;
  active: boolean;     // true during edgeguard phase
}

const CHARGE_BAR_WIDTH = 30;
const CHARGE_BAR_HEIGHT = 4;
const AIM_LINE_LENGTH = 200;

export class RailgunBeamRenderer {
  private container: Container;
  private beamGfx: Graphics;
  private chargeGfx: Graphics;
  private chargeFlashTimer = 0;

  constructor(parent: Container, zIndex: number) {
    this.container = new Container();
    this.container.zIndex = zIndex;
    this.beamGfx = new Graphics();
    this.beamGfx.blendMode = 'add';
    this.chargeGfx = new Graphics();
    this.chargeGfx.blendMode = 'add';
    this.container.addChild(this.beamGfx);
    this.container.addChild(this.chargeGfx);
    parent.addChild(this.container);
  }

  update(beam: BeamState | null, charge?: EdgeguardChargeState): void {
    this.beamGfx.clear();
    this.chargeGfx.clear();

    let hasContent = false;

    // === Charge bar + aim line (before fire) ===
    if (charge?.active && !charge.fired) {
      hasContent = true;

      const cosA = Math.cos(charge.attackerAngle);
      const sinA = Math.sin(charge.attackerAngle);
      // Turret tip position
      const tipX = charge.attackerX + cosA * charge.turretLength;
      const tipY = charge.attackerY - sinA * charge.turretLength;

      // Charge bar — positioned above turret tip, perpendicular to turret
      const barOffsetX = -sinA * 12; // perpendicular offset
      const barOffsetY = -cosA * 12;
      const barCx = tipX + barOffsetX;
      const barCy = tipY + barOffsetY;

      // Bar background (dark)
      const perpX = -sinA;
      const perpY = -cosA;
      const halfW = CHARGE_BAR_WIDTH / 2;

      // Background stroke
      this.chargeGfx.moveTo(barCx - perpX * halfW, barCy - perpY * halfW);
      this.chargeGfx.lineTo(barCx + perpX * halfW, barCy + perpY * halfW);
      this.chargeGfx.stroke({ color: 0x002222, width: CHARGE_BAR_HEIGHT + 2, alpha: 0.6, cap: 'round' });

      // Fill stroke (cyan → white as charge progresses)
      const fillW = halfW * charge.chargeT;
      if (fillW > 0) {
        const fillColor = charge.charged ? 0xffffff : 0x00ffff;
        const fillAlpha = charge.charged ? 0.9 : 0.7;
        this.chargeGfx.moveTo(barCx - perpX * halfW, barCy - perpY * halfW);
        this.chargeGfx.lineTo(barCx - perpX * halfW + perpX * fillW * 2, barCy - perpY * halfW + perpY * fillW * 2);
        this.chargeGfx.stroke({ color: fillColor, width: CHARGE_BAR_HEIGHT, alpha: fillAlpha, cap: 'round' });
      }

      // Flash pulse when fully charged
      if (charge.charged) {
        this.chargeFlashTimer += 1 / 60;
        const pulse = 0.5 + 0.5 * Math.sin(this.chargeFlashTimer * 12);
        // Glow ring at turret tip
        this.chargeGfx.circle(tipX, tipY, 8 + pulse * 4);
        this.chargeGfx.fill({ color: 0x00ffff, alpha: 0.15 + pulse * 0.15 });
        this.chargeGfx.circle(tipX, tipY, 4 + pulse * 2);
        this.chargeGfx.fill({ color: 0xffffff, alpha: 0.3 + pulse * 0.2 });
      } else {
        this.chargeFlashTimer = 0;
      }

      // Aim line — subtle direction indicator (only when charged)
      if (charge.charged) {
        const lineEndX = tipX + cosA * AIM_LINE_LENGTH;
        const lineEndY = tipY - sinA * AIM_LINE_LENGTH;

        // Faded dashed look: draw with low alpha
        this.chargeGfx.moveTo(tipX, tipY);
        this.chargeGfx.lineTo(lineEndX, lineEndY);
        this.chargeGfx.stroke({ color: 0x00ffff, width: 1.5, alpha: 0.25, cap: 'round' });

        // Brighter near turret
        const nearEndX = tipX + cosA * 40;
        const nearEndY = tipY - sinA * 40;
        this.chargeGfx.moveTo(tipX, tipY);
        this.chargeGfx.lineTo(nearEndX, nearEndY);
        this.chargeGfx.stroke({ color: 0x00ffff, width: 2, alpha: 0.5, cap: 'round' });
      }
    }

    // === Beam display (after fire) ===
    if (beam && beam.path.length >= 2 && beam.timer > 0) {
      hasContent = true;
      const alpha = beam.timer / beam.maxTimer;
      const path = beam.path;

      // 4-pass glow effect (Game Off style: outer glow → mid → cyan core → white)
      const passes = [
        { wMult: 3.0, a: alpha * 0.15, color: beam.color },
        { wMult: 2.0, a: alpha * 0.3, color: beam.color },
        { wMult: 1.0, a: alpha * 0.7, color: 0xaaffff },
        { wMult: 0.4, a: alpha * 1.0, color: 0xffffff },
      ];

      for (const pass of passes) {
        this.beamGfx.moveTo(path[0].x, path[0].y);
        for (let i = 1; i < path.length; i++) {
          this.beamGfx.lineTo(path[i].x, path[i].y);
        }
        this.beamGfx.stroke({
          color: pass.color,
          width: beam.width * pass.wMult,
          alpha: pass.a,
          cap: 'round',
          join: 'round',
        });
      }

      // Bounce point circles (white dots at each bend)
      for (let i = 1; i < path.length - 1; i++) {
        const p = path[i];
        this.beamGfx.circle(p.x, p.y, 10);
        this.beamGfx.fill({ color: 0xffffff, alpha: alpha * 0.9 });
      }

      // Terminus — expanding explosion circle (grows as beam fades)
      const terminus = path[path.length - 1];
      const explodeR = 15 + (1 - alpha) * 35;
      this.beamGfx.circle(terminus.x, terminus.y, explodeR);
      this.beamGfx.fill({ color: 0xffffff, alpha: alpha * 0.6 });
      // Inner bright core
      this.beamGfx.circle(terminus.x, terminus.y, explodeR * 0.4);
      this.beamGfx.fill({ color: beam.color, alpha: alpha * 0.9 });
    }

    this.container.visible = hasContent;
  }

  destroy(): void {
    this.container.destroy({ children: true });
  }
}
